import useFoodMenu from "./useFoodMenu";
import FoodMenuPanel from "./FoodMenuPanel";
import {calculateFoodPending} from "../../services/billingService";
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import CheckoutConfirmModal from "./CheckoutConfirmModal";
import InvoiceModal from "./InvoiceModal";
import PaymentReceiptModal from "./PaymentReceiptModal";
import RegistrationCardModal from "./RegistrationCardModal";
import FoodBillModal from "./FoodBillModal";
import GuestDetailsForm from "./GuestDetailsForm";
import {
  loadGuestProfiles,
} from "../../services/guestProfileService";

import {
  calculateBill,
  createInvoiceNumber,
  createReceiptNumber,
  roundMoney,
  toAmount,
} from "../../services/billingService";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const formatMoney = (value) =>
  `₹${toAmount(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getDateObject = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getGuestCount = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return stay.guests.length;
  }

  return Math.max(
    1,
    toAmount(
      stay.adults ??
        stay.guestCount ??
        1
    )
  );
};

const getPrimaryGuest = (stay = {}) => {
  if (
    Array.isArray(stay.guests) &&
    stay.guests.length > 0
  ) {
    return (
      stay.guests.find(
        (guest) =>
          guest?.isPrimary === true
      ) ||
      stay.guests[0]
    );
  }

  return {
    name:
      stay.guestName ||
      stay.name ||
      "Guest",

    phoneNumber:
      stay.phoneNumber ||
      stay.phone ||
      "",

    address: stay.address || "",

    idType: stay.idType || "",

    idNumber: stay.idNumber || "",

    idFrontUrl:
      stay.idFrontUrl || "",

    idBackUrl:
      stay.idBackUrl || "",
  };
};

const getInitialAdvancePayment = (
  stay = {}
) => {
  const advance = Math.max(
    0,
    toAmount(stay.advance)
  );

  if (advance <= 0) {
    return null;
  }

  return {
    amount: advance,

    quantity: 1,

    rate: advance,

    title: "Advance Received",

    note: "Check-In Advance",

    source: "check_in_advance",

    paymentMode:
      stay.paymentMode || "Cash",

    transactionReference:
      clean(
        stay.transactionReference
      ),

    receiptNumber:
      clean(
        stay.advanceReceiptNumber
      ),

    addedAt:
      stay.checkInAt ||
      stay.createdAtIso ||
      "",
  };
};

const getStableItemKey = (
  item,
  index
) =>
  clean(
    item?.receiptNumber ||
      item?.id ||
      item?.addedAt
  ) ||
  `${index}-${item?.amount || 0}`;

const createStableInvoiceNumber = (
  stay,
  hotelId
) =>
  clean(stay?.invoiceNumber) ||
  createInvoiceNumber(hotelId);


const createEmptyAdditionalGuest =
  () => ({
    profileId: "",
    searchTerm: "",
    name: "",
    phoneNumber: "",
    age: "",
    gender: "",
    nationality: "Indian",
    address: "",
    relation:
      "Additional Guest",
    idType:
      "Aadhaar Card",
    idNumber: "",
    idFrontFile: null,
    idBackFile: null,
    idFrontUrl: "",
    idBackUrl: "",
    sameAddressAsPrimary:
      false,
    visitCount: 0,
    isVip: false,
    isBlacklisted: false,
    blacklistReason: "",
    isPrimary: false,
  });

const getProfileId = (
  profile
) =>
  clean(
    profile?.profileId ||
      profile?.id ||
      profile?.guestProfileId
  );

const profileToAdditionalGuest =
  (
    profile,
    currentGuest
  ) => ({
    ...currentGuest,
    profileId:
      getProfileId(profile),
    searchTerm:
      profile?.name ||
      profile?.phoneNumber ||
      profile?.phone ||
      profile?.idNumber ||
      "",
    name:
      clean(profile?.name),
    phoneNumber:
      clean(
        profile?.phoneNumber ||
          profile?.phone
      ).replace(/\D/g, ""),
    age:
      profile?.age === undefined ||
      profile?.age === null
        ? ""
        : String(profile.age),
    gender:
      clean(profile?.gender),
    nationality:
      clean(profile?.nationality) ||
      "Indian",
    address:
      clean(profile?.address),
    relation:
      clean(profile?.relation) ||
      "Additional Guest",
    idType:
      clean(profile?.idType) ||
      "Aadhaar Card",
    idNumber:
      clean(profile?.idNumber),
    idFrontUrl:
      clean(profile?.idFrontUrl),
    idBackUrl:
      clean(profile?.idBackUrl),
    idFrontFile: null,
    idBackFile: null,
    visitCount:
      Number(
        profile?.visitCount || 0
      ),
    isVip:
      profile?.isVip === true ||
      profile?.isVIP === true ||
      profile?.guestTag === "vip",
    isBlacklisted:
      profile?.isBlacklisted === true ||
      profile?.blacklisted === true ||
      profile?.guestTag === "blacklist",
    blacklistReason:
      clean(profile?.blacklistReason),
    isPrimary: false,
  });

/* -------------------------------------------------------------------------- */
/*                              Reusable Field                                */
/* -------------------------------------------------------------------------- */

function Field({
  label,
  helpText = "",
  children,
}) {
  return (
    <label style={fieldStyle}>
      <div style={fieldLabelRowStyle}>
        <span style={fieldLabelStyle}>
          {label}
        </span>

        {helpText && (
          <span style={fieldHelpStyle}>
            {helpText}
          </span>
        )}
      </div>

      {children}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/*                           Occupied Room Modal                              */
/* -------------------------------------------------------------------------- */

function OccupiedRoomModal({
  stay,
  saving = false,

  hotelId,
  hotelName,

  onClose,
  onAddEntry,
  onContinueStay,
  onCheckout,
  onRoomShift,
  onAddGuest,
  availableRooms = [],
}) {
  const foodMenu = useFoodMenu(hotelId || stay?.hotelId);
  const [menuItemId, setMenuItemId] = useState('');
  const [foodMenuOpen, setFoodMenuOpen] = useState(false);
  const foodOperation = useRef(null);
  const selectedFoodItem = foodMenu.items.find(item => item.id === menuItemId && item.active !== false);
  const [type, setType] =
    useState("food");

  const [title, setTitle] =
    useState("");

  const [quantity, setQuantity] =
    useState("1");

  const [rate, setRate] =
    useState("");

  const [
    paymentMode,
    setPaymentMode,
  ] = useState("Cash");

  const [
    transactionReference,
    setTransactionReference,
  ] = useState("");

  const [note, setNote] =
    useState("");

  const [
    discountType,
    setDiscountType,
  ] = useState(
    stay?.discountType || "flat"
  );

  const [
    discountValue,
    setDiscountValue,
  ] = useState(
    stay?.discountType === "percent"
      ? String(
          stay?.discountValue || ""
        )
      : String(
          stay?.discountAmount || ""
        )
  );

  const [gstRate, setGstRate] =
    useState(
      String(stay?.gstRate ?? 0)
    );

  const [
    invoiceOpen,
    setInvoiceOpen,
  ] = useState(false);

  const [
    registrationOpen,
    setRegistrationOpen,
  ] = useState(false);

  const [
    foodBillOpen,
    setFoodBillOpen,
  ] = useState(false);

  const [
    checkoutConfirmOpen,
    setCheckoutConfirmOpen,
  ] = useState(false);

  const [
    cnuOpen,
    setCnuOpen,
  ] = useState(false);

  const [
    roomShiftOpen,
    setRoomShiftOpen,
  ] = useState(false);

  const [
    addGuestOpen,
    setAddGuestOpen,
  ] = useState(false);

  const [
    guestProfiles,
    setGuestProfiles,
  ] = useState([]);

  const [
    guestProfilesLoading,
    setGuestProfilesLoading,
  ] = useState(false);

  const [
    newGuest,
    setNewGuest,
  ] = useState(
    createEmptyAdditionalGuest
  );

  const [
    usePrimaryGuestPhone,
    setUsePrimaryGuestPhone,
  ] = useState(false);

  const [
    receiptPayment,
    setReceiptPayment,
  ] = useState(null);

  const [
    localSaving,
    setLocalSaving,
  ] = useState(false);

  const isSaving =
    saving || localSaving;

  const primaryGuest = useMemo(
    () => getPrimaryGuest(stay),
    [stay]
  );

  const primaryGuestPhone =
    clean(
      primaryGuest?.phoneNumber ||
        primaryGuest?.phone ||
        stay?.phoneNumber ||
        stay?.phone ||
        ""
    )
      .replace(/\D/g, "")
      .slice(0, 15);

  useEffect(() => {
    if (!addGuestOpen) {
      setUsePrimaryGuestPhone(false);
    }
  }, [addGuestOpen]);

  useEffect(() => {
    if (
      !addGuestOpen ||
      !hotelId
    ) {
      return;
    }

    let cancelled =
      false;

    setGuestProfilesLoading(
      true
    );

    loadGuestProfiles({
      hotelId,
      includeBlacklisted: true,
    })
      .then((profiles) => {
        if (!cancelled) {
          setGuestProfiles(
            Array.isArray(profiles)
              ? profiles
              : []
          );
        }
      })
      .catch((error) => {
        console.error(
          "Additional guest profiles load error:",
          error
        );
        if (!cancelled) {
          setGuestProfiles([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setGuestProfilesLoading(
            false
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    addGuestOpen,
    hotelId,
  ]);

  const toLocalDateTimeInput = (value) => {
    const date = value ? new Date(value) : new Date();
    if (Number.isNaN(date.getTime())) return "";
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
  };

  const defaultCnuCheckout = () => {
    const now = new Date();
    const date = new Date(now);
    date.setDate(date.getDate() + 1);
    date.setHours(11, 0, 0, 0);
    return toLocalDateTimeInput(date);
  };

  /* ------------------------------------------------------------------------ */
  /*                            Stable Invoice                                 */
  /* ------------------------------------------------------------------------ */

  const invoiceNumber = useMemo(
    () =>
      createStableInvoiceNumber(
        stay,
        hotelId
      ),
    [
      stay?.invoiceNumber,
      hotelId,
    ]
  );

  /* ------------------------------------------------------------------------ */
  /*                       Discount and GST State Sync                         */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    setDiscountType(
      stay?.discountType || "flat"
    );

    setDiscountValue(
      stay?.discountType === "percent"
        ? String(
            stay?.discountValue || ""
          )
        : String(
            stay?.discountAmount || ""
          )
    );

    setGstRate(
      String(stay?.gstRate ?? 0)
    );
  }, [
    stay?.discountType,
    stay?.discountValue,
    stay?.discountAmount,
    stay?.gstRate,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                           Bill Calculation                                */
  /* ------------------------------------------------------------------------ */

  const grossBeforeDiscount =
    toAmount(stay?.tariff) +
    toAmount(stay?.foodTotal) +
    toAmount(stay?.laundryTotal) +
    toAmount(stay?.extraTotal);

  const calculatedDiscount =
    useMemo(() => {
      const value = Math.max(
        0,
        toAmount(discountValue)
      );

      if (
        discountType === "percent"
      ) {
        const percentage =
          Math.min(100, value);

        return roundMoney(
          Math.min(
            grossBeforeDiscount,
            (grossBeforeDiscount *
              percentage) /
              100
          )
        );
      }

      return roundMoney(
        Math.min(
          grossBeforeDiscount,
          value
        )
      );
    }, [
      discountType,
      discountValue,
      grossBeforeDiscount,
    ]);

  const bill = useMemo(
    () =>
      calculateBill(stay, {
        discountAmount:
          calculatedDiscount,

        discountType,

        discountValue:
          toAmount(discountValue),

        gstRate:
          toAmount(gstRate),
      }),
    [
      stay,
      calculatedDiscount,
      discountType,
      discountValue,
      gstRate,
    ]
  );

  const foodTotal =
    roundMoney(
      toAmount(
        stay?.foodTotal
      )
    );

  const foodPaidTotal =
    roundMoney(
      toAmount(
        stay?.foodPaidTotal
      )
    );

  const pendingFoodBill = calculateFoodPending(stay);

  /* ------------------------------------------------------------------------ */
  /*                           Entry Calculation                               */
  /* ------------------------------------------------------------------------ */

  const entryAmount =
    type === "payment"
      ? roundMoney(
          toAmount(rate)
        )
      : roundMoney(
          toAmount(quantity) *
            toAmount(rate)
        );

  /* ------------------------------------------------------------------------ */
  /*                            Payment History                                */
  /* ------------------------------------------------------------------------ */

  const payments = useMemo(() => {
    const paymentList =
      Array.isArray(stay?.payments)
        ? [...stay.payments]
        : [];

    const advancePayment =
      getInitialAdvancePayment(stay);

    const advanceAlreadyExists =
      paymentList.some(
        (payment) =>
          payment?.source ===
            "check_in_advance" ||
          payment?.source ===
            "check_in_source_prepaid" ||
          payment?.source ===
            "check_in_guest_payment" ||
          clean(payment?.note) ===
            "Check-In Advance" ||
          clean(payment?.title) ===
            "Advance Received"
      );

    if (
      advancePayment &&
      !advanceAlreadyExists
    ) {
      paymentList.unshift(
        advancePayment
      );
    }

    return paymentList
      .map((payment, index) => ({
        ...payment,

        _localKey:
          getStableItemKey(
            payment,
            index
          ),
      }))
      .reverse();
  }, [stay]);

  useEffect(() => {
    if (type !== 'food') return;
    setTitle(selectedFoodItem?.name || '');
    setRate(selectedFoodItem ? String(selectedFoodItem.price) : '');
  }, [type, selectedFoodItem]);

  /* ------------------------------------------------------------------------ */
  /*                            Reset Entry Form                               */
  /* ------------------------------------------------------------------------ */

  const resetEntryForm = () => {
    setMenuItemId('');
    foodOperation.current = null;
    setTitle("");
    setQuantity("1");
    setRate("");
    setNote("");
    setTransactionReference("");
  };

  /* ------------------------------------------------------------------------ */
  /*                              Change Tab                                   */
  /* ------------------------------------------------------------------------ */

  const changeType = (nextType) => {
    if (isSaving) {
      return;
    }

    setType(nextType);
    resetEntryForm();

    if (nextType !== "payment") {
      setPaymentMode("Cash");
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                             Submit Entry                                  */
  /* ------------------------------------------------------------------------ */

  const submitEntry = async (
    event
  ) => {
    event.preventDefault();

    if (
      isSaving ||
      typeof onAddEntry !==
        "function"
    ) {
      return;
    }

    if (type === 'food' && (!selectedFoodItem || foodMenu.error || foodMenu.loading || !Number.isSafeInteger(Number(quantity)) || Number(quantity) < 1 || Number(quantity) > 999)) {
      alert('Food item select karein aur quantity 1–999 enter karein.');
      return;
    }
    if (
      type !== "payment" &&
      !clean(title)
    ) {
      alert(
        "Item ya charge ka naam enter karo."
      );

      return;
    }

    if (
      type !== "payment" &&
      toAmount(quantity) <= 0
    ) {
      alert(
        "Quantity minimum 1 honi chahiye."
      );

      return;
    }

    if (entryAmount <= 0) {
      alert(
        "Valid amount enter karo."
      );

      return;
    }

    if (
      type === "payment" &&
      paymentMode !== "Cash" &&
      !clean(
        transactionReference
      )
    ) {
      alert(
        "Online/Card payment ka transaction reference enter karo."
      );

      return;
    }

    const payload = {
      type,

      title:
        type === "payment"
          ? "Payment Received"
          : clean(title),

      quantity:
        type === "payment"
          ? 1
          : Math.max(
              1,
              toAmount(quantity)
            ),

      rate:
        type === "payment"
          ? entryAmount
          : Math.max(
              0,
              toAmount(rate)
            ),

      amount: entryAmount,

      paymentMode:
        type === "payment"
          ? clean(paymentMode)
          : "",

      transactionReference:
        type === "payment"
          ? clean(
              transactionReference
            )
          : "",

      note: clean(note),

      source:
        type === "payment"
          ? "room_payment"
          : `room_${type}`,
    };

    if (type === 'food') {
      const key = JSON.stringify([stay?.stayId || stay?.id, menuItemId, selectedFoodItem.revision, quantity, note]);
      if (foodOperation.current?.key !== key) foodOperation.current = {key, id: window.crypto?.randomUUID?.() || `FOOD_${Date.now()}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`};
      Object.assign(payload, {menuItemId, menuRevision: selectedFoodItem.revision, foodOrderId: foodOperation.current.id});
    }
    setLocalSaving(true);

    try {
      await onAddEntry(payload);

      resetEntryForm();

      alert(
        type === "payment"
          ? "Payment add ho gaya ✅"
          : type === "food" ? "Food order placed — Pending delivery ✅" : "Charge add ho gaya ✅"
      );
    } catch (error) {
      console.error(
        "Occupied room entry error:",
        error
      );

      alert(
        "Entry save nahi hui: " +
          (error?.message ||
            error)
      );
    } finally {
      setLocalSaving(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                         Save Discount and GST                             */
  /* ------------------------------------------------------------------------ */

  const saveDiscountAndGst =
    async () => {
      if (
        isSaving ||
        typeof onAddEntry !==
          "function"
      ) {
        return;
      }

      const inputValue = Math.max(
        0,
        toAmount(discountValue)
      );

      if (
        discountType === "percent" &&
        inputValue > 100
      ) {
        alert(
          "Discount percentage 100% se zyada nahi ho sakta."
        );

        return;
      }

      const payload = {
        type: "discount",

        title:
          discountType === "percent"
            ? `Discount ${inputValue}%`
            : "Flat Discount",

        amount:
          calculatedDiscount,

        discountType,

        discountValue:
          inputValue,

        discountAmount:
          calculatedDiscount,

        gstRate:
          Math.max(
            0,
            toAmount(gstRate)
          ),

        note: clean(note),

        source:
          "room_discount_gst",
      };

      setLocalSaving(true);

      try {
        await onAddEntry(payload);

        setNote("");

        alert(
          "Discount aur GST save ho gaya ✅"
        );
      } catch (error) {
        console.error(
          "Discount/GST save error:",
          error
        );

        alert(
          "Discount/GST save nahi hua: " +
            (error?.message ||
              error)
        );
      } finally {
        setLocalSaving(false);
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                          Open Checkout Modal                              */
  /* ------------------------------------------------------------------------ */

  const openCheckoutConfirmation =
    () => {
    if (
      pendingFoodBill > 0
    ) {
      alert(
        `Food Bill ${formatMoney(
          pendingFoodBill
        )} pending hai. Pehle Food Bill open karke payment receive karo.`
      );

      setFoodBillOpen(true);
      return;
    }

      if (isSaving) {
        return;
      }

      setCheckoutConfirmOpen(true);
    };

  /* ------------------------------------------------------------------------ */
  /*                          Confirm Final Checkout                           */
  /* ------------------------------------------------------------------------ */

  const confirmFinalCheckout =
    async (checkoutPayload) => {
      if (
        isSaving ||
        typeof onCheckout !==
          "function"
      ) {
        return;
      }

      setLocalSaving(true);

      try {
        await onCheckout({
          ...bill,
          ...checkoutPayload,

          invoiceNumber:
            clean(
              checkoutPayload
                ?.invoiceNumber
            ) || invoiceNumber,

          actualCheckOutAt:
            checkoutPayload
              ?.actualCheckOutAt ||
            new Date().toISOString(),

          allowPendingCheckout:
            Boolean(
              checkoutPayload
                ?.allowPendingCheckout
            ),

          pendingCheckoutOverride:
            Boolean(
              checkoutPayload
                ?.pendingCheckoutOverride
            ),

          checkoutPayment:
            Math.max(
              0,
              toAmount(
                checkoutPayload
                  ?.checkoutPayment
              )
            ),

          checkoutPaymentEntry:
            checkoutPayload
              ?.checkoutPaymentEntry ||
            null,

          damageCharge:
            Math.max(
              0,
              toAmount(
                checkoutPayload
                  ?.damageCharge
              )
            ),

          damageNote:
            clean(
              checkoutPayload
                ?.damageNote
            ),

          grossTotal:
            toAmount(
              checkoutPayload
                ?.grossTotal ??
                bill.grossTotal
            ),

          discount:
            toAmount(
              checkoutPayload
                ?.discount ??
                bill.discount
            ),

          discountAmount:
            toAmount(
              checkoutPayload
                ?.discountAmount ??
                checkoutPayload
                  ?.discount ??
                bill.discount
            ),

          taxableAmount:
            toAmount(
              checkoutPayload
                ?.taxableAmount ??
                bill.taxableAmount
            ),

          gstRate:
            toAmount(
              checkoutPayload
                ?.gstRate ??
                bill.gstRate
            ),

          gstAmount:
            toAmount(
              checkoutPayload
                ?.gstAmount ??
                bill.gstAmount
            ),

          cgst:
            toAmount(
              checkoutPayload?.cgst ??
                bill.cgst
            ),

          sgst:
            toAmount(
              checkoutPayload?.sgst ??
                bill.sgst
            ),

          grandTotal:
            toAmount(
              checkoutPayload
                ?.grandTotal ??
                bill.grandTotal
            ),

          paid:
            toAmount(
              checkoutPayload?.paid ??
                bill.paid
            ),

          paidTotal:
            toAmount(
              checkoutPayload
                ?.paidTotal ??
                checkoutPayload
                  ?.paid ??
                bill.paid
            ),

          balance:
            Math.max(
              0,
              toAmount(
                checkoutPayload
                  ?.balance
              )
            ),

          excessPayment:
            Math.max(
              0,
              toAmount(
                checkoutPayload
                  ?.excessPayment
              )
            ),

          pendingReason:
            clean(
              checkoutPayload
                ?.pendingReason
            ),

          managerNote:
            clean(
              checkoutPayload
                ?.managerNote
            ),

          roomKeysReceived:
            checkoutPayload
              ?.roomKeysReceived ===
            true,

          guestSignatureReceived:
            checkoutPayload
              ?.guestSignatureReceived ===
            true,

          idDocumentsVerified:
            checkoutPayload
              ?.idDocumentsVerified ===
            true,

          roomCheckedForDamage:
            checkoutPayload
              ?.roomCheckedForDamage ===
            true,

          sendRoomForCleaning:
            checkoutPayload
              ?.sendRoomForCleaning !==
            false,

          printInvoiceAfterCheckout:
            checkoutPayload
              ?.printInvoiceAfterCheckout !==
            false,

          printReceiptAfterCheckout:
            checkoutPayload
              ?.printReceiptAfterCheckout ===
            true,
        });

        setCheckoutConfirmOpen(false);
      } catch (error) {
        console.error(
          "Final checkout error:",
          error
        );

        alert(
          "Checkout complete nahi hua: " +
            (error?.message ||
              error)
        );

        throw error;
      } finally {
        setLocalSaving(false);
      }
    };

  /* ------------------------------------------------------------------------ */
  /*                          Keyboard Close Support                           */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.key === "Escape" &&
        !isSaving &&
        !invoiceOpen &&
        !registrationOpen &&
        !checkoutConfirmOpen &&
        !receiptPayment
      ) {
        onClose?.();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
  }, [
    isSaving,
    invoiceOpen,
    registrationOpen,
    checkoutConfirmOpen,
    receiptPayment,
    onClose,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  if (!stay) {
    return null;
  }

  return (
    <div
      style={overlayStyle}
      onMouseDown={(event) => {
        if (
          event.target ===
            event.currentTarget &&
          !isSaving
        ) {
          onClose?.();
        }
      }}
    >
      {foodMenuOpen && <FoodMenuPanel hotelId={hotelId || stay?.hotelId} onClose={() => setFoodMenuOpen(false)}/>}
      <div style={modalStyle}>
        <header style={headerStyle}>
          <div>
            <div style={roomTitleRowStyle}>
              <h2 style={roomTitleStyle}>
                Room {stay.room || "-"}
              </h2>

              <span style={occupiedBadgeStyle}>
                Occupied
              </span>
            </div>

            <div style={guestNameStyle}>
              {primaryGuest?.name ||
                stay.guestName ||
                "Guest"}
            </div>

            <div style={guestMetaStyle}>
              📞{" "}
              {primaryGuest?.phoneNumber ||
                stay.phoneNumber ||
                "-"}{" "}
              • 👥 {getGuestCount(stay)}{" "}
              Guest
              {getGuestCount(stay) !== 1
                ? "s"
                : ""}
            </div>
          </div>

          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              onClose?.()
            }
            style={{
              ...closeButtonStyle,

              opacity: isSaving
                ? 0.5
                : 1,

              cursor: isSaving
                ? "not-allowed"
                : "pointer",
            }}
          >
            ×
          </button>
        </header>

        <section style={stayInfoGridStyle}>
          <InfoBox
            label="A&D Serial"
            value={
              stay.adRegisterSerialNo ||
              stay.adSerialNo ||
              "-"
            }
          />

          <InfoBox
            label="Check-In"
            value={formatDateTime(
              stay.checkInAt
            )}
          />

          <InfoBox
            label="Expected Checkout"
            value={formatDateTime(
              stay.expectedCheckOutAt
            )}
          />

          <InfoBox
            label="Stay Type"
            value={stay.stayType || "-"}
          />

          <InfoBox
            label="Booking Source"
            value={
              stay.bookingSource || "-"
            }
          />

          <InfoBox
            label="Payment Mode"
            value={
              stay.paymentMode || "-"
            }
          />
        </section>

        <section style={summaryGridStyle}>
          <SummaryCard
            label="Room Rent"
            value={bill.roomRent}
          />

          <SummaryCard
            label="Food"
            value={bill.food}
          />

          <SummaryCard
            label="Food Pending"
            value={
              pendingFoodBill
            }
            danger={
              pendingFoodBill > 0
            }
          />

          <SummaryCard
            label="Laundry"
            value={bill.laundry}
          />

          <SummaryCard
            label="Extra"
            value={bill.extra}
          />

          {toAmount(stay.extraPersonCharge) > 0 && (
            <SummaryCard
              label={`Extra Person${
                toAmount(stay.extraPersonCount) > 0
                  ? ` (${toAmount(stay.extraPersonCount)})`
                  : ""
              }`}
              value={stay.extraPersonCharge}
            />
          )}

          <SummaryCard
            label="Paid"
            value={bill.paid}
          />

          <SummaryCard
            label="Room / Other Balance"
            value={bill.balance}
            strong
            danger={bill.balance > 0}
          />
        </section>

        <section style={quickActionsStyle}>
          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              setRegistrationOpen(true)
            }
            style={{
              ...quickButtonStyle,
              background: "#2563eb",
            }}
          >
            🪪 Registration / IDs
          </button>

          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              setFoodBillOpen(true)
            }
            style={{
              ...quickButtonStyle,
              background:
                pendingFoodBill > 0
                  ? "#ea580c"
                  : "#16a34a",
            }}
          >
            🍴 Food Bill
            {pendingFoodBill > 0
              ? ` • ${formatMoney(
                  pendingFoodBill
                )}`
              : " • Clear"}
          </button>

          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              setCnuOpen(true)
            }
            style={{
              ...quickButtonStyle,
              background: "#7c3aed",
            }}
          >
            🔄 CNU / Continue Stay
          </button>

          <button
            type="button"
            disabled={isSaving}
            onClick={() =>
              setInvoiceOpen(true)
            }
            style={{
              ...quickButtonStyle,
              background: "#0f766e",
            }}
          >
            🧾 Invoice Preview
          </button>

          <button
            type="button"
            disabled={isSaving || availableRooms.length === 0}
            onClick={() =>
              setRoomShiftOpen(true)
            }
            style={{
              ...quickButtonStyle,
              background: "#0891b2",
              opacity:
                availableRooms.length === 0
                  ? 0.55
                  : 1,
            }}
            title={
              availableRooms.length === 0
                ? "Koi vacant room available nahi hai"
                : "Guest ko doosre vacant room me shift karo"
            }
          >
            🔁 Room Shift
          </button>
          <button
            type="button"
            disabled={isSaving}
            onClick={() => {
              setNewGuest(
                createEmptyAdditionalGuest()
              );
              setAddGuestOpen(
                true
              );
            }}
            style={{
              ...quickButtonStyle,
              background: "#7c3aed",
              opacity:
                isSaving
                  ? 0.6
                  : 1,
            }}
            title="Already checked-in room me baad me aaye guest ko add karo"
          >
            👥 Add Guest
          </button>
        </section>

        <section style={tabsStyle}>
          {[
            ["food", "🍴 Food"],
            [
              "laundry",
              "🧺 Laundry",
            ],
            ["extra", "➕ Extra"],
            [
              "payment",
              "💳 Payment",
            ],
            [
              "discount",
              "🏷 Discount & GST",
            ],
          ].map(
            ([tabValue, label]) => (
              <button
                key={tabValue}
                type="button"
                disabled={isSaving}
                onClick={() =>
                  changeType(tabValue)
                }
                style={{
                  ...tabButtonStyle,

                  background:
                    type === tabValue
                      ? "#1d4ed8"
                      : "#e2e8f0",

                  color:
                    type === tabValue
                      ? "#ffffff"
                      : "#334155",

                  opacity: isSaving
                    ? 0.6
                    : 1,
                }}
              >
                {label}
              </button>
            )
          )}
        </section>

        {type === "discount" ? (
          <section style={formSectionStyle}>
            <h3 style={sectionHeadingStyle}>
              Discount & GST
            </h3>

            <div style={formGridStyle}>
              <Field label="Discount Type">
                <select
                  value={discountType}
                  disabled={isSaving}
                  onChange={(event) =>
                    setDiscountType(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                >
                  <option value="flat">
                    Flat Discount ₹
                  </option>

                  <option value="percent">
                    Percentage %
                  </option>
                </select>
              </Field>

              <Field
                label="Discount Value"
                helpText={
                  discountType ===
                  "percent"
                    ? "Maximum 100%"
                    : formatMoney(
                        calculatedDiscount
                      )
                }
              >
                <input
                  type="number"
                  min="0"
                  max={
                    discountType ===
                    "percent"
                      ? 100
                      : undefined
                  }
                  value={discountValue}
                  disabled={isSaving}
                  onChange={(event) =>
                    setDiscountValue(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Discount"
                />
              </Field>

              <Field label="GST Rate">
                <select
                  value={gstRate}
                  disabled={isSaving}
                  onChange={(event) =>
                    setGstRate(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                >
                  <option value="0">
                    GST 0%
                  </option>

                  <option value="5">
                    GST 5%
                  </option>

                  <option value="12">
                    GST 12%
                  </option>

                  <option value="18">
                    GST 18%
                  </option>
                </select>
              </Field>

              <Field label="Reason / Note">
                <input
                  value={note}
                  disabled={isSaving}
                  onChange={(event) =>
                    setNote(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Discount approval / reason"
                />
              </Field>
            </div>

            <button
              type="button"
              disabled={isSaving}
              onClick={
                saveDiscountAndGst
              }
              style={{
                ...primaryButtonStyle,

                opacity: isSaving
                  ? 0.6
                  : 1,
              }}
            >
              {isSaving
                ? "Saving..."
                : `Save Discount ${formatMoney(
                    calculatedDiscount
                  )} & GST ${toAmount(
                    gstRate
                  )}%`}
            </button>
          </section>
        ) : (
          <form
            onSubmit={submitEntry}
            style={formSectionStyle}
          >
            <h3 style={sectionHeadingStyle}>
              {type === "food"
                ? "Add Food Order"
                : type === "laundry"
                ? "Add Laundry"
                : type === "extra"
                ? "Add Extra Charge"
                : "Receive Payment"}
            </h3>

            <div style={formGridStyle}>
              {type !== "payment" && (
                <Field
                  label={
                    type === "food"
                      ? "Food Item"
                      : type ===
                        "laundry"
                      ? "Laundry Item"
                      : "Extra Charge"
                  }
                >
                  {type === 'food' ? <>
                    <select aria-label="Food Item" value={menuItemId} disabled={isSaving || foodMenu.loading || !!foodMenu.error} style={inputStyle} onChange={event => setMenuItemId(event.target.value)}>
                      <option value="">{foodMenu.loading ? 'Loading menu…' : 'Select food item'}</option>
                      {[...new Set(foodMenu.items.filter(item => item.active !== false).map(item => item.category))].map(category => <optgroup label={category} key={category}>
                        {foodMenu.items.filter(item => item.active !== false && item.category === category).map(item => <option value={item.id} key={item.id}>{item.name}{item.unit ? ` (${item.unit})` : ''} — ₹{item.price}</option>)}
                      </optgroup>)}
                    </select>
                    {foodMenu.error && <p role="alert" style={{color:'#b91c1c'}}>{foodMenu.error}</p>}
                    {!foodMenu.loading && !foodMenu.error && !foodMenu.items.some(item => item.active !== false) && <p>Is hotel ka menu pehle setup karein.</p>}
                    <button type="button" style={{padding:8,borderRadius:8,border:'1px solid #cbd5e1',background:'#fff',color:'#0f172a',marginTop:8}} disabled={isSaving} onClick={() => setFoodMenuOpen(true)}>Menu Setup / Edit Rates</button>
                  </> : <input
                    value={title}
                    disabled={isSaving}
                    onChange={(event) =>
                      setTitle(
                        event.target.value
                      )
                    }
                    style={inputStyle}
                    placeholder={
                      type === "food"
                        ? "Tea, Water, Veg Thali..."
                        : type ===
                          "laundry"
                        ? "Shirt, Bedsheet..."
                        : "Extra Mattress, Early Check-In..."
                    }
                  />}
                </Field>
              )}

              {type !== "payment" && (
                <Field label="Quantity">
                  <input
                    type="number"
                    min="1"
                    max={type === 'food' ? 999 : undefined}
                    step={type === 'food' ? 1 : 'any'}
                    aria-label="Quantity"
                    value={quantity}
                    disabled={isSaving}
                    onChange={(event) =>
                      setQuantity(
                        event.target.value
                      )
                    }
                    style={inputStyle}
                  />
                </Field>
              )}

              <Field
                label={
                  type === "payment"
                    ? "Payment Amount"
                    : "Rate"
                }
                helpText={formatMoney(
                  entryAmount
                )}
              >
                <input
                  type="number"
                  min="0"
                  aria-label={type === 'food' ? 'Saved Food Rate' : type === 'payment' ? 'Payment Amount' : 'Rate'}
                  value={type === 'food' ? selectedFoodItem?.price ?? '' : rate}
                  readOnly={type === 'food'}
                  disabled={isSaving}
                  onChange={(event) =>
                    setRate(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="₹"
                />
              </Field>

              {type === "payment" && (
                <Field label="Payment Mode">
                  <select
                    value={paymentMode}
                    disabled={isSaving}
                    onChange={(event) => {
                      const mode =
                        event.target.value;

                      setPaymentMode(mode);

                      if (mode === "Cash") {
                        setTransactionReference(
                          ""
                        );
                      }
                    }}
                    style={inputStyle}
                  >
                    <option>Cash</option>
                    <option>UPI</option>
                    <option>Card</option>
                    <option>
                      Bank Transfer
                    </option>
                    <option>Fab</option>
                    <option>Brevistay</option>
                    <option>Other OTA</option>
                  </select>
                </Field>
              )}

              {type === "payment" &&
                paymentMode !== "Cash" && (
                  <Field label="Transaction Reference">
                    <input
                      value={
                        transactionReference
                      }
                      disabled={isSaving}
                      onChange={(event) =>
                        setTransactionReference(
                          event.target.value
                        )
                      }
                      style={inputStyle}
                      placeholder="UPI / Card / Bank transaction code"
                    />
                  </Field>
                )}

              <Field label="Note">
                <input
                  value={note}
                  disabled={isSaving}
                  onChange={(event) =>
                    setNote(
                      event.target.value
                    )
                  }
                  style={inputStyle}
                  placeholder="Optional note"
                />
              </Field>
            </div>

            <button
              disabled={isSaving}
              style={{
                ...primaryButtonStyle,

                opacity: isSaving
                  ? 0.6
                  : 1,

                cursor: isSaving
                  ? "not-allowed"
                  : "pointer",
              }}
            >
              {isSaving
                ? "Saving..."
                : `${type === "food" ? "Place Order" : type === "payment" ? "Add Payment" : "Add Charge"} • ${formatMoney(entryAmount)}`}
            </button>
          </form>
        )}

        <section style={billBoxStyle}>
          <h3 style={sectionHeadingStyle}>
            Current Bill
          </h3>

          <BillRow
            label="Room Rent"
            value={bill.roomRent}
          />

          <BillRow
            label="Food"
            value={bill.food}
          />

          <BillRow
            label="Laundry"
            value={bill.laundry}
          />

          <BillRow
            label="Extra Charges"
            value={bill.extra}
          />

          <div style={dividerStyle} />

          <BillRow
            label="Gross Total"
            value={bill.grossTotal}
          />

          <BillRow
            label="Discount"
            value={-bill.discount}
          />

          <BillRow
            label="Taxable Amount"
            value={bill.taxableAmount}
          />

          <BillRow
            label={`GST (${bill.gstRate}%)`}
            value={bill.gstAmount}
          />

          {bill.gstAmount > 0 && (
            <>
              <BillRow
                label="CGST"
                value={bill.cgst}
              />

              <BillRow
                label="SGST"
                value={bill.sgst}
              />
            </>
          )}

          <div style={dividerStyle} />

          <BillRow
            label="Grand Total"
            value={bill.grandTotal}
            strong
          />

          <BillRow
            label="Total Paid"
            value={bill.paid}
          />

          <BillRow
            label="Room / Other Balance"
            value={bill.balance}
            strong
            danger={bill.balance > 0}
          />

          {bill.excessPaid > 0 && (
            <BillRow
              label="Excess Paid"
              value={bill.excessPaid}
              warning
            />
          )}
        </section>

        <section style={historySectionStyle}>
          <h3 style={sectionHeadingStyle}>
            Payment History
          </h3>

          {payments.length === 0 ? (
            <div style={emptyStateStyle}>
              Abhi koi payment entry nahi
              hai.
            </div>
          ) : (
            payments.map(
              (payment) => (
                <div
                  key={payment._localKey}
                  style={historyRowStyle}
                >
                  <div>
                    <strong
                      style={
                        historyAmountStyle
                      }
                    >
                      {formatMoney(
                        payment.amount
                      )}
                    </strong>

                    <div
                      style={
                        historyMetaStyle
                      }
                    >
                      {payment.paymentMode ||
                        "-"}{" "}
                      •{" "}
                      {formatDateTime(
                        payment.addedAt
                      )}
                    </div>

                    {clean(
                      payment.transactionReference
                    ) && (
                      <div
                        style={
                          transactionStyle
                        }
                      >
                        Ref:{" "}
                        {
                          payment.transactionReference
                        }
                      </div>
                    )}

                    {clean(payment.note) && (
                      <div
                        style={
                          historyNoteStyle
                        }
                      >
                        {payment.note}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={() =>
                      setReceiptPayment(
                        payment
                      )
                    }
                    style={receiptButtonStyle}
                  >
                    Receipt
                  </button>
                </div>
              )
            )
          )}
        </section>

        <button
          type="button"
          disabled={isSaving}
          onClick={
            openCheckoutConfirmation
          }
          style={{
            ...checkoutButtonStyle,

            opacity: isSaving
              ? 0.6
              : 1,

            cursor: isSaving
              ? "not-allowed"
              : "pointer",
          }}
        >
          {isSaving
            ? "Processing..."
            : bill.balance > 0
            ? `🚪 Checkout • ${formatMoney(
                bill.balance
              )} Balance`
            : "🚪 Proceed to Checkout"}
        </button>

        {addGuestOpen && (
          <div
            style={
              nestedOverlayStyle
            }
            onMouseDown={(
              event
            ) => {
              if (
                event.target ===
                  event.currentTarget &&
                !isSaving
              ) {
                setAddGuestOpen(
                  false
                );
              }
            }}
          >
            <div
              style={
                addGuestModalStyle
              }
            >
              <div
                style={
                  nestedModalHeaderStyle
                }
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: 20,
                    }}
                  >
                    👥 Add Guest to Room{" "}
                    {clean(
                      stay?.room
                    )}
                  </h3>

                  <div
                    style={{
                      marginTop: 4,
                      color:
                        "#64748b",
                      fontSize: 12,
                    }}
                  >
                    Existing stay same rahega. Sirf naya guest registration/ID is room me add hoga.
                  </div>
                </div>

                <button
                  type="button"
                  disabled={
                    isSaving
                  }
                  onClick={() =>
                    setAddGuestOpen(
                      false
                    )
                  }
                  style={
                    closeButtonStyle
                  }
                >
                  ×
                </button>
              </div>

              {guestProfilesLoading && (
                <div
                  style={{
                    marginBottom: 10,
                    padding: 9,
                    borderRadius: 8,
                    background:
                      "#eff6ff",
                    color:
                      "#1d4ed8",
                    fontSize: 11,
                    fontWeight: 800,
                  }}
                >
                  Existing guest profiles load ho rahe hain...
                </div>
              )}

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 12,
                  padding: "11px 12px",
                  border: "1px solid #cbd5e1",
                  borderRadius: 10,
                  background:
                    usePrimaryGuestPhone
                      ? "#eff6ff"
                      : "#f8fafc",
                  cursor:
                    isSaving ||
                    !primaryGuestPhone
                      ? "not-allowed"
                      : "pointer",
                  opacity:
                    !primaryGuestPhone
                      ? 0.65
                      : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={
                    usePrimaryGuestPhone
                  }
                  disabled={
                    isSaving ||
                    !primaryGuestPhone
                  }
                  onChange={(event) => {
                    const checked =
                      event.target.checked;

                    if (
                      checked &&
                      !primaryGuestPhone
                    ) {
                      alert(
                        "Primary guest ka phone number available nahi hai."
                      );
                      return;
                    }

                    setUsePrimaryGuestPhone(
                      checked
                    );

                    setNewGuest(
                      (current) => ({
                        ...current,
                        phoneNumber:
                          checked
                            ? primaryGuestPhone
                            : current.phoneNumber ===
                              primaryGuestPhone
                            ? ""
                            : current.phoneNumber,
                      })
                    );
                  }}
                  style={{
                    width: 18,
                    height: 18,
                    cursor:
                      isSaving ||
                      !primaryGuestPhone
                        ? "not-allowed"
                        : "pointer",
                  }}
                />

                <div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 900,
                      color: "#0f172a",
                    }}
                  >
                    Use Primary Guest Number
                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 11,
                      color: "#64748b",
                    }}
                  >
                    {primaryGuestPhone
                      ? `Primary guest: ${primaryGuestPhone}`
                      : "Primary guest ka phone number available nahi hai."}
                  </div>
                </div>
              </label>

              <GuestDetailsForm
                guest={
                  newGuest
                }
                index={0}
                profiles={
                  guestProfiles
                }
                disabled={
                  isSaving
                }
                titleOverride="Additional Guest"
                primaryAddress={
                  primaryGuest?.address ||
                  stay?.address ||
                  ""
                }
                onChange={(
                  _index,
                  field,
                  value
                ) => {
                  if (
                    field ===
                      "phoneNumber" &&
                    usePrimaryGuestPhone &&
                    String(value || "")
                      .replace(/\D/g, "")
                      .slice(0, 15) !==
                      primaryGuestPhone
                  ) {
                    setUsePrimaryGuestPhone(
                      false
                    );
                  }

                  setNewGuest(
                    (current) => ({
                      ...current,
                      [field]:
                        value,
                    })
                  );
                }}
                onSelectProfile={(
                  _index,
                  profile
                ) => {
                  setNewGuest(
                    (current) => {
                      const nextGuest =
                        profileToAdditionalGuest(
                          profile,
                          current
                        );

                      return {
                        ...nextGuest,
                        phoneNumber:
                          usePrimaryGuestPhone &&
                          primaryGuestPhone
                            ? primaryGuestPhone
                            : nextGuest.phoneNumber,
                      };
                    }
                  );

                  if (
                    profile?.isBlacklisted === true ||
                    profile?.guestTag === "blacklist"
                  ) {
                    alert(
                      `Warning ⚠️\n\n${profile.name || "Guest"} blacklisted hai.\n\nReason: ${profile.blacklistReason || "Reason available nahi hai."}`
                    );
                  }
                }}
                onRemove={() => {}}
              />

              <div
                style={
                  nestedModalFooterStyle
                }
              >
                <button
                  type="button"
                  disabled={
                    isSaving
                  }
                  onClick={() =>
                    setAddGuestOpen(
                      false
                    )
                  }
                  style={{
                    ...cnuCancelButtonStyle,
                    minWidth: 120,
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  disabled={
                    isSaving
                  }
                  onClick={async () => {
                    if (
                      typeof onAddGuest !==
                      "function"
                    ) {
                      alert(
                        "Add Guest handler missing hai."
                      );
                      return;
                    }

                    setLocalSaving(true);

                    try {
                      const guestToAdd = {
                        ...newGuest,
                        phoneNumber:
                          usePrimaryGuestPhone &&
                          primaryGuestPhone
                            ? primaryGuestPhone
                            : newGuest.phoneNumber,
                      };

                      await onAddGuest(
                        guestToAdd
                      );

                      setNewGuest(
                        createEmptyAdditionalGuest()
                      );
                      setUsePrimaryGuestPhone(
                        false
                      );
                      setAddGuestOpen(
                        false
                      );
                    } finally {
                      setLocalSaving(
                        false
                      );
                    }
                  }}
                  style={{
                    ...primaryButtonStyle,
                    minWidth: 160,
                    background:
                      "#7c3aed",
                  }}
                >
                  {isSaving
                    ? "Saving..."
                    : "✓ Add Guest"}
                </button>
              </div>
            </div>
          </div>
        )}

        {roomShiftOpen && (
          <RoomShiftModal
            stay={stay}
            rooms={availableRooms}
            saving={isSaving}
            onClose={() =>
              !isSaving &&
              setRoomShiftOpen(false)
            }
            onSave={async (payload) => {
              if (
                typeof onRoomShift !==
                "function"
              ) {
                alert(
                  "Room shift handler missing hai."
                );
                return;
              }

              setLocalSaving(true);

              try {
                await onRoomShift(payload);
                setRoomShiftOpen(false);
              } finally {
                setLocalSaving(false);
              }
            }}
          />
        )}

        {cnuOpen && (
          <ContinueStayModal
            stay={stay}
            saving={isSaving}
            defaultExpectedCheckOutAt={defaultCnuCheckout()}
            onClose={() => !isSaving && setCnuOpen(false)}
            onSave={async (payload) => {
              if (typeof onContinueStay !== "function") {
                alert("CNU handler missing hai.");
                return;
              }
              setLocalSaving(true);
              try {
                await onContinueStay(payload);
                setCnuOpen(false);
              } finally {
                setLocalSaving(false);
              }
            }}
          />
        )}

        {checkoutConfirmOpen && (
          <CheckoutConfirmModal
            stay={{
              ...stay,

              hotelId:
                stay.hotelId ||
                hotelId,

              hotelName:
                stay.hotelName ||
                hotelName,

              invoiceNumber:
                stay.invoiceNumber ||
                invoiceNumber,
            }}
            bill={{
              ...bill,
              invoiceNumber,
            }}
            hotelId={hotelId}
            hotelName={
              hotelName ||
              stay.hotelName
            }
            saving={isSaving}
            onClose={() => {
              if (!isSaving) {
                setCheckoutConfirmOpen(
                  false
                );
              }
            }}
            onConfirm={
              confirmFinalCheckout
            }
          />
        )}

        {foodBillOpen && (
          <FoodBillModal
            stay={stay}
            hotelName={
              hotelName ||
              stay?.hotelName
            }
            saving={isSaving}
            onClose={() => {
              if (!isSaving) {
                setFoodBillOpen(
                  false
                );
              }
            }}
            onReceivePayment={async (
              payment
            ) => {
              if (
                typeof onAddEntry !==
                "function"
              ) {
                return;
              }

              await onAddEntry(
                payment
              );
            }}
          />
        )}

        {registrationOpen && (
          <RegistrationCardModal
            stay={stay}
            hotelName={
              hotelName ||
              stay.hotelName
            }
            title="Registration Card"
            successMessage="Current occupied room ka registration record aur ID documents."
            onClose={() =>
              setRegistrationOpen(
                false
              )
            }
          />
        )}

        {invoiceOpen && (
          <InvoiceModal
            stay={{
              ...stay,
              invoiceNumber,
            }}
            bill={bill}
            invoiceNumber={
              invoiceNumber
            }
            hotelName={
              hotelName ||
              stay.hotelName
            }
            onClose={() =>
              setInvoiceOpen(false)
            }
          />
        )}

        {receiptPayment && (
          <PaymentReceiptModal
            stay={stay}
            payment={
              receiptPayment
            }
            hotelName={
              hotelName ||
              stay.hotelName
            }
            receiptNumber={
              receiptPayment
                .receiptNumber ||
              createReceiptNumber(
                hotelId
              )
            }
            onClose={() =>
              setReceiptPayment(null)
            }
          />
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function RoomShiftModal({
  stay,
  rooms = [],
  saving = false,
  onClose,
  onSave,
}) {
  const currentRoom = clean(stay?.room);
  const [toRoom, setToRoom] = useState("");
  const [reason, setReason] = useState("");
  const [oldRoomStatus, setOldRoomStatus] =
    useState("dirty");

  const roomOptions = useMemo(
    () =>
      (rooms || [])
        .map((room) =>
          clean(
            typeof room === "object"
              ? room?.room
              : room
          )
        )
        .filter(
          (room) =>
            room && room !== currentRoom
        )
        .sort((a, b) =>
          a.localeCompare(b, undefined, {
            numeric: true,
          })
        ),
    [rooms, currentRoom]
  );

  const submitShift = async (event) => {
    event.preventDefault();

    const targetRoom = clean(toRoom);

    if (!targetRoom) {
      alert("Shift ke liye room select karo.");
      return;
    }

    if (targetRoom === currentRoom) {
      alert("Current aur new room same nahi ho sakte.");
      return;
    }

    const confirmed = window.confirm(
      `Room shift confirm karna hai?\n\n${currentRoom} → ${targetRoom}\n\nGuest, bill, payments, IDs aur current stay new room me shift ho jayenge.`
    );

    if (!confirmed) return;

    if (typeof onSave === "function") {
      await onSave({
        fromRoom: currentRoom,
        toRoom: targetRoom,
        reason: clean(reason),
        oldRoomStatus,
      });
    }
  };

  return (
    <div style={nestedOverlayStyle}>
      <form
        onSubmit={submitShift}
        style={cnuModalStyle}
      >
        <div style={cnuHeaderStyle}>
          <div>
            <h2 style={{ margin: 0 }}>
              🔁 Room Shift
            </h2>
            <div style={cnuSubtitleStyle}>
              Room {currentRoom} ka poora active
              stay doosre vacant room me transfer
              hoga.
            </div>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            style={closeButtonStyle}
          >
            ×
          </button>
        </div>

        <div style={cnuInfoBoxStyle}>
          Guest, registration/IDs, tariff,
          food/laundry/extra bill, payment history,
          invoice aur stay ID same rahenge. Sirf
          active room change hoga.
        </div>

        <div style={cnuGridStyle}>
          <Field label="Current Room">
            <input
              value={currentRoom}
              disabled
              style={{
                ...inputStyle,
                background: "#f1f5f9",
                fontWeight: 900,
              }}
            />
          </Field>

          <Field label="Shift To Vacant Room">
            <select
              value={toRoom}
              disabled={saving}
              onChange={(event) =>
                setToRoom(event.target.value)
              }
              style={inputStyle}
              required
            >
              <option value="">
                Select vacant room
              </option>
              {roomOptions.map((room) => (
                <option key={room} value={room}>
                  {room}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Old Room Status">
            <select
              value={oldRoomStatus}
              disabled={saving}
              onChange={(event) =>
                setOldRoomStatus(
                  event.target.value
                )
              }
              style={inputStyle}
            >
              <option value="dirty">
                Dirty / Checkout
              </option>
              <option value="service">
                Service / Blocked
              </option>
              <option value="ready">
                Ready / Vacant
              </option>
            </select>
          </Field>

          <Field label="Reason / Note">
            <input
              value={reason}
              disabled={saving}
              onChange={(event) =>
                setReason(event.target.value)
              }
              style={inputStyle}
              placeholder="AC issue / washroom / guest request..."
            />
          </Field>
        </div>

        <div style={cnuActionStyle}>
          <button
            type="button"
            disabled={saving}
            onClick={onClose}
            style={cnuCancelButtonStyle}
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={
              saving ||
              !toRoom ||
              roomOptions.length === 0
            }
            style={{
              ...cnuSaveButtonStyle,
              background: "#0891b2",
              opacity:
                saving || !toRoom
                  ? 0.6
                  : 1,
            }}
          >
            {saving
              ? "Shifting..."
              : `Shift ${currentRoom} → ${
                  toRoom || "Room"
                }`}
          </button>
        </div>
      </form>
    </div>
  );
}

function ContinueStayModal({
  stay,
  saving = false,
  defaultExpectedCheckOutAt = "",
  onClose,
  onSave,
}) {
  const [tariff, setTariff] = useState("");
  const [received, setReceived] = useState("");
  const [paymentMode, setPaymentMode] = useState("Cash");
  const [transactionReference, setTransactionReference] = useState("");
  const [expectedCheckOutAt, setExpectedCheckOutAt] = useState(defaultExpectedCheckOutAt);
  const [note, setNote] = useState("");

  const cnuTariff = Math.max(0, toAmount(tariff));
  const amountReceived = Math.max(0, toAmount(received));
  const existingBalance = Math.max(0, toAmount(stay?.balance));
  const availableAdvance = Math.max(
    0,
    toAmount(
      stay?.advanceCreditBalance ??
        stay?.excessAdvanceCredit ??
        stay?.excessAdvanceReceived ??
        0
    )
  );

  const advanceAppliedPreview = Math.min(
    cnuTariff,
    availableAdvance
  );

  const dueBeforeFresh = Math.max(
    0,
    roundMoney(
      existingBalance +
        cnuTariff -
        advanceAppliedPreview
    )
  );

  const projectedBalance = Math.max(
    0,
    roundMoney(dueBeforeFresh - amountReceived)
  );

  const projectedFreshAdvance = Math.max(
    0,
    roundMoney(amountReceived - dueBeforeFresh)
  );

  const projectedAdvanceLeft = Math.max(
    0,
    roundMoney(
      availableAdvance -
        advanceAppliedPreview +
        projectedFreshAdvance
    )
  );

  const save = async () => {
    if (saving) return;
    if (cnuTariff <= 0) return alert("CNU tariff enter karo.");

    const expected = new Date(expectedCheckOutAt);
    if (
      !expectedCheckOutAt ||
      Number.isNaN(expected.getTime()) ||
      expected <= new Date()
    ) {
      return alert("Valid future expected checkout date/time select karo.");
    }

    if (
      amountReceived > 0 &&
      paymentMode !== "Cash" &&
      !clean(transactionReference)
    ) {
      return alert("Online/FAB/Brevistay payment ka reference/code enter karo.");
    }

    await onSave?.({
      tariff: cnuTariff,
      amountReceived,
      paymentMode: amountReceived > 0 ? paymentMode : "Pending",
      transactionReference:
        amountReceived > 0 ? clean(transactionReference) : "",
      expectedCheckOutAt: expected.toISOString(),
      note: clean(note),
      continuedAt: new Date().toISOString(),
    });
  };

  return (
    <div style={nestedOverlayStyle}>
      <div style={cnuModalStyle}>
        <div style={cnuHeaderStyle}>
          <div>
            <h2 style={{ margin: 0 }}>🔄 CNU / Continue Stay</h2>
            <div style={cnuSubtitleStyle}>
              Room {stay?.room || "-"} • A&D {stay?.adRegisterSerialNo || stay?.adSerialNo || "-"} • {stay?.guestName || "Guest"}
            </div>
          </div>
          <button type="button" disabled={saving} onClick={onClose} style={closeButtonStyle}>×</button>
        </div>

        <div style={cnuInfoBoxStyle}>
          Same Sr. No., same Room aur same Guest continue rahega. Purana advance available hai to CNU tariff me <b>automatic adjust</b> hoga. Previous Day Book row ka OUT <b>CNU</b> hoga aur present business day me new CNU row banegi.
        </div>

        {availableAdvance > 0 && (
          <div style={{ ...cnuInfoBoxStyle, background: "#ecfdf5", borderColor: "#a7f3d0", color: "#166534" }}>
            Available Advance: <b>{formatMoney(availableAdvance)}</b> • Is CNU me auto use: <b>{formatMoney(advanceAppliedPreview)}</b>
          </div>
        )}

        <div style={cnuGridStyle}>
          <Field label="CNU Tariff ₹">
            <input type="number" min="0" step="0.01" value={tariff} disabled={saving} onChange={(e) => setTariff(e.target.value)} style={inputStyle} placeholder="3500" />
          </Field>
          <Field label="Amount Received Now ₹">
            <input type="number" min="0" step="0.01" value={received} disabled={saving} onChange={(e) => setReceived(e.target.value)} style={inputStyle} placeholder={availableAdvance > 0 ? "0 = Advance auto adjust" : "0 = Pending"} />
          </Field>
          <Field label="Payment Mode">
            <select value={paymentMode} disabled={saving || amountReceived <= 0} onChange={(e) => setPaymentMode(e.target.value)} style={inputStyle}>
              <option value="Cash">Cash</option>
              <option value="UPI">UPI / Online</option>
              <option value="Fab">Fab</option>
              <option value="Brevistay">Brevistay</option>
            </select>
          </Field>
          <Field label="Expected Checkout">
            <input type="datetime-local" value={expectedCheckOutAt} disabled={saving} onChange={(e) => setExpectedCheckOutAt(e.target.value)} style={inputStyle} />
          </Field>
        </div>

        {amountReceived > 0 && paymentMode !== "Cash" && (
          <Field label="Transaction / Reference Code">
            <input value={transactionReference} disabled={saving} onChange={(e) => setTransactionReference(e.target.value)} style={inputStyle} placeholder="UPI / FAB / Brevistay reference" />
          </Field>
        )}

        <Field label="Note (Optional)">
          <input value={note} disabled={saving} onChange={(e) => setNote(e.target.value)} style={inputStyle} placeholder="Same day checkout / special rate / approval..." />
        </Field>

        <div style={{ ...cnuSummaryGridStyle, gridTemplateColumns: "repeat(auto-fit,minmax(125px,1fr))" }}>
          <div style={cnuSummaryBoxStyle}><small>CNU Tariff</small><strong>{formatMoney(cnuTariff)}</strong></div>
          <div style={cnuSummaryBoxStyle}><small>Advance Used</small><strong style={{ color: advanceAppliedPreview > 0 ? "#7c3aed" : "#0f172a" }}>{formatMoney(advanceAppliedPreview)}</strong></div>
          <div style={cnuSummaryBoxStyle}><small>Received Now</small><strong>{formatMoney(amountReceived)}</strong></div>
          <div style={cnuSummaryBoxStyle}><small>Total Balance</small><strong style={{ color: projectedBalance > 0 ? "#dc2626" : "#15803d" }}>{formatMoney(projectedBalance)}</strong></div>
          <div style={cnuSummaryBoxStyle}><small>Advance Left</small><strong style={{ color: projectedAdvanceLeft > 0 ? "#15803d" : "#0f172a" }}>{formatMoney(projectedAdvanceLeft)}</strong></div>
        </div>

        <div style={cnuActionStyle}>
          <button type="button" disabled={saving} onClick={onClose} style={cnuCancelButtonStyle}>Cancel</button>
          <button type="button" disabled={saving} onClick={save} style={cnuSaveButtonStyle}>{saving ? "Saving CNU..." : "✓ Confirm CNU"}</button>
        </div>
      </div>
    </div>
  );
}

function InfoBox({
  label,
  value,
}) {
  return (
    <div style={infoBoxStyle}>
      <span style={infoLabelStyle}>
        {label}
      </span>

      <strong style={infoValueStyle}>
        {value}
      </strong>
    </div>
  );
}

function SummaryCard({
  label,
  value,
  strong = false,
  danger = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <div style={summaryLabelStyle}>
        {label}
      </div>

      <div
        style={{
          ...summaryValueStyle,

          fontSize: strong
            ? 19
            : 16,

          color: danger
            ? "#dc2626"
            : "#0f172a",
        }}
      >
        {formatMoney(value)}
      </div>
    </div>
  );
}

function BillRow({
  label,
  value,
  strong = false,
  danger = false,
  warning = false,
}) {
  return (
    <div
      style={{
        ...billRowStyle,

        fontSize: strong
          ? 17
          : 13,

        fontWeight: strong
          ? 900
          : 600,

        color: danger
          ? "#dc2626"
          : warning
          ? "#c2410c"
          : "#334155",
      }}
    >
      <span>{label}</span>

      <span>
        {formatMoney(value)}
      </span>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const nestedOverlayStyle = { position: "fixed", inset: 0, zIndex: 45000, display: "flex", alignItems: "center", justifyContent: "center", padding: 14, background: "rgba(15,23,42,.72)" };
const cnuModalStyle = { width: "min(760px,100%)", maxHeight: "94vh", overflowY: "auto", padding: 18, borderRadius: 18, background: "#fff", boxSizing: "border-box" };
const cnuHeaderStyle = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 12 };
const cnuSubtitleStyle = { marginTop: 5, color: "#64748b", fontSize: 11, fontWeight: 700 };
const cnuInfoBoxStyle = { marginBottom: 12, padding: 10, border: "1px solid #ddd6fe", borderRadius: 10, background: "#f5f3ff", color: "#5b21b6", fontSize: 10, lineHeight: 1.5 };
const cnuGridStyle = { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10 };
const cnuSummaryGridStyle = { display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 12 };
const cnuSummaryBoxStyle = { display: "grid", gap: 4, padding: 10, border: "1px solid #e2e8f0", borderRadius: 10, background: "#f8fafc" };
const cnuActionStyle = { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 };
const cnuCancelButtonStyle = { border: "1px solid #cbd5e1", borderRadius: 9, padding: "10px 14px", background: "#fff", color: "#475569", fontWeight: 800, cursor: "pointer" };
const cnuSaveButtonStyle = { border: 0, borderRadius: 9, padding: "10px 15px", background: "#7c3aed", color: "#fff", fontWeight: 900, cursor: "pointer" };

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 12000,

  display: "flex",
  alignItems: "center",
  justifyContent: "center",

  padding: 12,

  background:
    "rgba(15,23,42,.74)",
};

const modalStyle = {
  width: "min(920px,100%)",
  maxHeight: "95vh",

  overflowY: "auto",

  padding: 20,

  borderRadius: 20,

  background: "#ffffff",

  boxSizing: "border-box",

  boxShadow:
    "0 25px 80px rgba(0,0,0,.32)",
};

const headerStyle = {
  position: "sticky",
  top: -20,
  zIndex: 20,

  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 12,

  margin: "-20px -20px 15px",
  padding: "17px 20px",

  borderBottom:
    "1px solid #e2e8f0",

  background:
    "rgba(255,255,255,.97)",

  backdropFilter: "blur(12px)",
};

const roomTitleRowStyle = {
  display: "flex",
  alignItems: "center",

  gap: 8,
  flexWrap: "wrap",
};

const roomTitleStyle = {
  margin: 0,
  color: "#0f172a",
};

const occupiedBadgeStyle = {
  padding: "4px 8px",

  borderRadius: 999,

  background: "#fee2e2",
  color: "#b91c1c",

  fontSize: 10,
  fontWeight: 900,
};

const guestNameStyle = {
  marginTop: 6,

  color: "#334155",

  fontSize: 15,
  fontWeight: 900,
};

const guestMetaStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 12,
};

const closeButtonStyle = {
  width: 38,
  height: 38,

  border: 0,
  borderRadius: 10,

  background: "#f1f5f9",
  color: "#334155",

  fontSize: 26,
  lineHeight: 1,
};

const stayInfoGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(145px,1fr))",

  gap: 9,

  marginTop: 4,
};

const infoBoxStyle = {
  padding: 10,

  borderRadius: 11,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const infoLabelStyle = {
  display: "block",

  marginBottom: 4,

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const infoValueStyle = {
  display: "block",

  color: "#334155",

  fontSize: 11,
  fontWeight: 900,

  overflowWrap: "anywhere",
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(110px,1fr))",

  gap: 8,

  marginTop: 14,
};

const summaryCardStyle = {
  padding: 10,

  borderRadius: 12,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",

  textAlign: "center",
};

const summaryLabelStyle = {
  color: "#64748b",

  fontSize: 10,
  fontWeight: 800,
};

const summaryValueStyle = {
  marginTop: 4,

  fontWeight: 900,
};

const quickActionsStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(180px,1fr))",

  gap: 10,

  marginTop: 14,
};

const quickButtonStyle = {
  minHeight: 44,

  padding: "10px 13px",

  border: 0,
  borderRadius: 10,

  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const tabsStyle = {
  display: "flex",
  flexWrap: "wrap",

  gap: 8,

  marginTop: 18,
};

const tabButtonStyle = {
  border: 0,

  padding: "9px 12px",

  borderRadius: 10,

  fontWeight: 800,
  cursor: "pointer",
};

const formSectionStyle = {
  marginTop: 16,

  padding: 14,

  borderRadius: 14,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const sectionHeadingStyle = {
  margin: "0 0 11px",

  color: "#0f172a",

  fontSize: 16,
};

const formGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(190px,1fr))",

  gap: 10,
};

const fieldStyle = {
  display: "block",
};

const fieldLabelRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 8,

  marginBottom: 6,
};

const fieldLabelStyle = {
  color: "#334155",

  fontSize: 12,
  fontWeight: 800,
};

const fieldHelpStyle = {
  color: "#64748b",

  fontSize: 10,
};

const inputStyle = {
  width: "100%",
  minHeight: 43,

  padding: 11,

  border:
    "1px solid #cbd5e1",

  borderRadius: 10,

  background: "#ffffff",
  color: "#0f172a",

  boxSizing: "border-box",

  fontSize: 13,

  outline: "none",
};

const primaryButtonStyle = {
  width: "100%",

  marginTop: 12,
  padding: 12,

  border: 0,
  borderRadius: 11,

  background: "#16a34a",
  color: "#ffffff",

  fontWeight: 900,
  cursor: "pointer",
};

const billBoxStyle = {
  marginTop: 18,

  padding: 14,

  borderRadius: 14,

  border:
    "1px solid #e2e8f0",

  background: "#f8fafc",
};

const billRowStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 12,

  padding: "6px 0",
};

const dividerStyle = {
  height: 1,

  margin: "7px 0",

  background: "#cbd5e1",
};

const historySectionStyle = {
  marginTop: 18,
};

const emptyStateStyle = {
  padding: 12,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 10,

  color: "#64748b",

  fontSize: 12,
  textAlign: "center",
};

const historyRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 10,

  padding: 10,

  border:
    "1px solid #e2e8f0",

  borderRadius: 10,

  marginBottom: 8,
};

const historyAmountStyle = {
  color: "#0f172a",
};

const historyMetaStyle = {
  marginTop: 3,

  color: "#64748b",

  fontSize: 11,
};

const transactionStyle = {
  display: "inline-block",

  marginTop: 4,
  padding: "3px 6px",

  borderRadius: 5,

  background: "#fef08a",
  color: "#854d0e",

  fontSize: 10,
  fontWeight: 900,
};

const historyNoteStyle = {
  marginTop: 3,

  color: "#475569",

  fontSize: 10,
};

const receiptButtonStyle = {
  border: 0,

  borderRadius: 8,

  padding: "8px 10px",

  background: "#334155",
  color: "#ffffff",

  fontWeight: 800,
  cursor: "pointer",
};

const checkoutButtonStyle = {
  width: "100%",

  marginTop: 14,
  padding: 13,

  border: 0,
  borderRadius: 11,

  background: "#ea580c",
  color: "#ffffff",

  fontWeight: 900,
};


const addGuestModalStyle = {
  width: "min(980px, 100%)",
  maxHeight: "94vh",
  overflowY: "auto",
  background: "#ffffff",
  borderRadius: 18,
  boxShadow:
    "0 24px 80px rgba(15,23,42,.28)",
  padding: 16,
};

const nestedModalHeaderStyle = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 12,
  marginBottom: 14,
  position: "sticky",
  top: 0,
  zIndex: 3,
  background: "#ffffff",
  paddingBottom: 10,
  borderBottom:
    "1px solid #e2e8f0",
};

const nestedModalFooterStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  flexWrap: "wrap",
  position: "sticky",
  bottom: 0,
  background: "#ffffff",
  paddingTop: 12,
  marginTop: 14,
  borderTop:
    "1px solid #e2e8f0",
};

export default OccupiedRoomModal;